import React, { useEffect, useState } from 'react';
import { useMutation } from '@apollo/client';
import { useLocation, useNavigate } from 'react-router-dom';
import { cloneDeep, isEqual, get } from 'lodash';
import { Cart, ICartPrices, ICartProps, ICartSummaryData, IEquipmentDetailsProps } from '@next-components/cart';
import { MessagebarSeverityLevel } from '@next-components/messagebar';
import {
  AccessoryType,
  EnumMapper,
  FinancingType,
  INextArticle,
  IOrder,
  IVehicleAccessory,
  IVehicleConfiguration,
  IConfiguration,
  MBApiCalculationFlag,
  mapAccessoryVatCode,
  isStockSaleOrderToBeProcessed,
} from 'next-common';
import {
  INotificationPageProps,
  IVehiclePageProps,
  IVehicleStock,
  Routes,
  toggleVehicleAccessoriesOnOrderMutationParsed,
  useConfigurations,
  useVehicleTyreAccessories,
} from '../../../common';
import {
  VehicleConfig,
  prepareVehicleConfig,
  prepareArticles,
  checkAndUpdateCart,
  calculateVatAndCreateInput,
  isOrderEditable,
  getSelectedEquipmentInCart,
  filterSelectedEquipmentInCart,
} from '../../../utils';
import {
  AccessoryTool,
  IAccessoryChange,
  AccessoryChangeAction,
  ICustomAccessoryProps,
  getDefaultOpenedCategoryForAccessories,
} from './AccessoryTool';
import { VehicleAccessoriesPageStyled } from './VehicleAccessoriesPage.styled';
import { TireSets } from './tire-sets';
import { AccessorySet } from './accessory-sets';
import { LoadingSpinner } from '@next-components/loading-spinner';
import { CustomAccessorySet } from './custom-accessory-sets';
import { useVatStateCalculationHandler } from '../../../common/hooks/useVatCalculation';
import { ErrorNotification, NextErrorNotification, ValidationErrorCode } from '../../notifications';
import { MarginCart } from '../../common-components/MarginCart';
import { IStepMenuData } from '../sales-wizard/sales-wizard-menu';
import { CustomMessageBar } from '../../common-components/custom-messagebar';
import { CustomAlertPopup } from '../../common-components/custom-DeletePopup';
import { IAlertPopup } from '../sales-wizard';
import { IconType } from '@next-components/bos-icon';
import { ArticleType } from 'sales-common';

export interface IVehicleAccessoriesPageProps extends IVehiclePageProps, INotificationPageProps {
  nonExactMatchCount?: number;
  exactMatchCount?: number;
  order?: IOrder;
  vehicle?: IVehicleConfiguration;
  isCalled?: boolean;
  cartData?: ICartProps;
  articles?: INextArticle[];
  availableVehicleList?: IVehicleStock[];
  selectedSerialNo?: number;
  vatPrices?: ICartPrices;
  stepMenuData?: IStepMenuData;
  isElectricCar?: boolean;
  isShowDeleteModel?: boolean;
  isShowDeleteButton?: boolean;
  isLoading?: boolean;
  deleteOrderData?: IAlertPopup;
  rootUrl?: string;
  isVehicleValidForReserveOrSign?: boolean;

  setIsCalled?(called: boolean): void;
  setIsLoading?(value: boolean): void;
  setIsShowDeleteModel?: (isShowDeleteModel: boolean) => void;
}

export const VehicleAccessoriesPage = (props: IVehicleAccessoriesPageProps) => {
  const [loading, setLoading] = useState(false);
  const [accessoryTool, setAccessoryTool] = useState<AccessoryTool>(null);
  const [changes, setChanges] = useState<IAccessoryChange[]>([]);
  const order: IOrder = props?.order;
  const vehicle: IVehicleConfiguration = props?.vehicle;
  const [cart, setCart] = useState<ICartProps>(props?.cartData);
  const [articles, setArticles] = useState<INextArticle[]>(props?.articles);
  const [toggleAccessoriesOnOrder] = useMutation(toggleVehicleAccessoriesOnOrderMutationParsed);
  const navigate = useNavigate();
  const location = useLocation();

  const [defaultOpenAccessories, setDefaultOpenAccessories] = useState<string[]>([]);
  const [isCustomAccessoryOpen, setCustomAccessoryOpen] = useState<boolean>(false);
  const configurations: IConfiguration = useConfigurations();
  const [orderEditable, setOrderEditable] = useState<boolean>(isOrderEditable(order, configurations));
  const carType = order?.pimSnapshot?.vehicleType ?? props?.vehicle?.model?.vehicleType ?? '';

  const { finalPrice } = useVatStateCalculationHandler({
    articles,
    carType,
  });

  const { tires, accessories, isTyreAccLoading } = useVehicleTyreAccessories({ 
    orderId: order?.id,
    source: order?.lead?.source,
    configuration: props?.configuration,
    serialNo: order?.vehicles?.[0]?.serialNo,
    make: order?.lead?.make,
  })

  /*
   * Any changes to order or vehicle data would trigger the following useEffect,
   * this usually happens on mount of component or any order mutation is called
   */
  useEffect(() => {
    if (!order) {
      return;
    }
    const isStockSaleOrder = isStockSaleOrderToBeProcessed(
      props.configuration,
      order?.lead?.source,
      order?.vehicles?.[0]?.serialNo || location?.state?.serialNo,
      order?.lead?.make,
    );

    if(!isStockSaleOrder && !vehicle) return;
    const accessoryInstance = new AccessoryTool(order, vehicle, tires, accessories, isStockSaleOrder);
    setAccessoryTool(accessoryInstance);
    resetAccessories();
    setDefaultOpenAccessories(getDefaultOpenedCategoryForAccessories(accessoryInstance?.mappedAccessories));
    setCustomAccessoryOpen(Boolean(accessoryInstance?.mappedCustomAccessories?.length));

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [order, vehicle, tires, accessories]);

  useEffect(() => {
    if (props?.isCalled && !changes?.length) {
      nextStep();
    }
    if (props?.isCalled && changes?.length) {
      saveAccessories();
    }
  }, [props?.isCalled]);

  useEffect(() => {
    props.setIsLoading(loading);
  }, [loading]);

  useEffect(() => {
    if (!isEqual(cart, props?.cartData)) {
      setCart(props.cartData);
    }
    if (!isEqual(articles, props?.articles)) {
      setArticles(props.articles);
    }
  }, [props?.cartData, props?.articles]);

  useEffect(() => {
    setOrderEditable(isOrderEditable(order, configurations));
  }, [order, configurations]);

  const nextStep = () => {
    const route = Routes.getFinancePage(props.orderId);
    props.setIsLoading(loading);
    props.setIsCalled(false);
    const redirectUrl = get(props?.stepMenuData, 'route') ? props?.stepMenuData?.route : route;
    navigate(redirectUrl);
  };

  const resetAccessories = (isResetButtonCliked = false): void => {
    if (isResetButtonCliked) {
      setCart(props?.cartData);
      setArticles(props?.articles);
    }
    setChanges([]);
  };

  const onToggleAccessoriesOnOrder = async (): Promise<IOrder> => {
    if (!order?.id) {
      return null;
    }
    setLoading(true);
    const vatCalculation = calculateVatAndCreateInput(articles, carType);
    if (!vatCalculation) {
      setLoading(false);
      props.raiseNotification(
        <ErrorNotification
          key="vatError"
          errorCode={ValidationErrorCode.VAT_VALIDATION}
          severityLevel={MessagebarSeverityLevel.ERROR}
          onClose={() => props?.clearNotifications('vatError')}
        />,
      );
      resetAccessories(true);
      return;
    }
    const removeAccesories = changes?.filter((change) => change?.action === AccessoryChangeAction.REMOVE);
    const removedAccessories = removeAccesories?.length ? accessoryTool?.getRemoveInput(removeAccesories) : [];

    const addAccessories = changes?.filter((change) => change?.action === AccessoryChangeAction.ADD);
    const addedAccessories: IVehicleAccessory[] = EnumMapper.mapEnumsToStringRecursively(
      addAccessories?.length ? accessoryTool.getAddInput(addAccessories, order) : [],
    );

    try {
      const variables = {
        orderId: order?.id,
        addAccessories: addedAccessories,
        removeAccessories: removedAccessories,
        vatCalculation,
        nextArticles:articles
      };
      //Articles
      const { data } = await toggleAccessoriesOnOrder({ variables });
      if (data?.toggleVehicleAccessoriesOnOrder?.__typename === 'NextError') {
        props.raiseNotification(
          <NextErrorNotification
            key={'toggleAccessoryFail'}
            orderId={order?.id}
            errorCode={data?.toggleVehicleAccessoriesOnOrder?.errorCode}
            onClose={() => props?.clearNotifications('toggleAccessoryFail')}
          />,
        );
      }
      setLoading(false);
      if (props?.isCalled) {
        nextStep();
      }
      return data?.toggleVehicleAccessoriesOnOrder;
    } catch (error) {
      console.error('Error toggling vehicle accessories to order', error);
      setLoading(false);
      return null;
    }
  };

  const saveAccessories = () => {
    onToggleAccessoriesOnOrder()
      .catch((error) => {
        console.error('An error occurred whilst updating accessories', error);
      })
      .finally(() => {
        resetAccessories();
      });
  };

  const updateTyreInCart = (tyreId: string): void => {
    const cartCopy: ICartProps = cloneDeep(cart);
    if (accessoryTool?.tireSetData?.length) {
      accessoryTool?.tireSetData?.forEach((tire) => {
        if (tire?.id === tyreId) {
          const vatCode = mapAccessoryVatCode(tire);
          const changeTyre = prepareVehicleConfig(
            `${VehicleConfig.TYRE}`,
            tire?.id,
            tire?.name,
            tire?.priceExcludingVat,
            vatCode,
          );
          setArticles([
            ...prepareArticles(
              `${VehicleConfig.TYRE}`,
              tire?.priceExcludingVat,
              vatCode,
              ArticleType.WINTERTIRES,
              tire?.retailPriceExcludingVat,
              false,
              articles,
            ),
          ]);
          checkAndUpdateCart(`${VehicleConfig.TYRE}`, cartCopy, setCart, changeTyre);
        }
      });
    }
  };

  const addNewOrRemoveInverseChange = (newChanges: IAccessoryChange[]) => {
    const prevChanges = [...changes];
    newChanges?.forEach(({ action, accessoryId, manualData }) => {
      const exisitingAction = prevChanges?.find((change) => change?.accessoryId === accessoryId);
      const inverseAction = action * -1;
      if (!exisitingAction) {
        prevChanges?.push({ accessoryId, action, manualData });
      } else if (exisitingAction?.action === inverseAction) {
        prevChanges?.splice(prevChanges?.indexOf(exisitingAction), 1);
      }
    });
    setChanges(prevChanges);
  };

  const raiseWarningForUnselectTire = () => {
    props.raiseNotification(
      <ErrorNotification
        key="removeLeasing"
        errorCode={ValidationErrorCode.UNABLE_TO_UNSELECT_TIRES}
        severityLevel={MessagebarSeverityLevel.WARNING}
        onClose={() => props?.clearNotifications('removeLeasing')}
      />,
    );
  };

  const switchTire = (from?: string, to?: string): void => {
    const switchChanges: IAccessoryChange[] = [];
    // Selected tire removed from PIM so take it from order
    if (!from) {
      from = order?.accessories?.find(
        (accessory) => accessory?.accessoryType?.toString() === AccessoryType[AccessoryType.TYRE],
      )?.customId;
    }
    if (from) {
      const fromChange: IAccessoryChange = { accessoryId: from, action: AccessoryChangeAction.REMOVE };
      switchChanges?.push(fromChange);
    }
    if (to) {
      updateTyreInCart(to);
      const toChange: IAccessoryChange = { accessoryId: to, action: AccessoryChangeAction.ADD };
      switchChanges?.push(toChange);
    }
    addNewOrRemoveInverseChange(switchChanges);
  };

  const removeAccessory = (
    id: string,
    accessory: ICustomAccessoryProps,
    accessoryType = AccessoryType.ACCESSORY,
  ): void => {
    const cartCopy: ICartProps = cloneDeep(cart);
    const accessoryKey =
      accessoryType === AccessoryType.TYRE ? `${VehicleConfig.TYRE}` : `${VehicleConfig.ACCESSORY}_${id || ''}`;
    const recommendedTireConfig: ICartSummaryData = prepareVehicleConfig(accessoryKey, id, '', 0, '');

    setArticles([
      ...prepareArticles(
        accessoryKey,
        accessory?.priceExcludingVat,
        accessory?.vatCode,
        ArticleType.ACCESSORY,
        accessory?.retailPriceExcludingVat,
        false,
        articles,
        true,
      ),
    ]);

    checkAndUpdateCart(accessoryKey, cartCopy, setCart, recommendedTireConfig, true);
    const change: IAccessoryChange = { accessoryId: id, action: AccessoryChangeAction.REMOVE };
    addNewOrRemoveInverseChange([change]);
  };

  const addAccessory = (accessoryId: string, accessory?: ICustomAccessoryProps) => {
    const cartCopy: ICartProps = cloneDeep(cart);
    const accessoryKey = `${VehicleConfig.ACCESSORY}_${accessoryId || ''}`;
    const vatCode = mapAccessoryVatCode(accessory, accessory?.isManualInput);

    const newAccessory: ICartSummaryData = prepareVehicleConfig(
      accessoryKey,
      accessory?.id,
      accessory?.name,
      accessory?.priceExcludingVat,
      vatCode,
    );
    setArticles([
      ...prepareArticles(
        accessoryKey,
        accessory?.priceExcludingVat,
        vatCode,
        ArticleType.ACCESSORY,
        accessory?.retailPriceExcludingVat,
        false,
        articles,
      ),
    ]);
    checkAndUpdateCart(accessoryKey, cartCopy, setCart, newAccessory);

    const manualData = accessory?.isManualInput
      ? {
          ...accessory,
          priceExcludingVat: accessory?.priceExcludingVat,
          priceIncludingVat: accessory?.price,
          vatCode,
        }
      : null;

    const change: IAccessoryChange = {
      accessoryId,
      action: AccessoryChangeAction.ADD,
      manualData,
    };

    addNewOrRemoveInverseChange([change]);
  };


  if (!order) {
    return <LoadingSpinner isModal={true} />;
  }

  const isLeasingOffer =
    get(order, 'chosenOffer')?.financingType === FinancingType.LEASING ||
    get(order, 'offers')?.some((offer) => offer?.financingType === FinancingType.LEASING);

    const handleClose = () => {
      props.setIsShowDeleteModel(false);
    };    
  return (
    <VehicleAccessoriesPageStyled className="mainContainer">
      <div className="leftContainer vehicleAccessoriesPage">
        <CustomMessageBar orderEditable={orderEditable} />
        <TireSets
          loading={loading}
          changes={changes}
          tireSetData={accessoryTool?.tireSetData}
          handleSwitchTire={switchTire}
          handleRemoveTire={removeAccessory}
          isLeasingOffer={isLeasingOffer}
          raiseWarningForUnselectTire={raiseWarningForUnselectTire}
        />
        {accessoryTool?.mappedAccessories && (
          <AccessorySet
            loading={loading}
            orderId={order?.id}
            accessories={accessoryTool?.mappedAccessories}
            defaultOpenAccessories={defaultOpenAccessories}
            changes={changes}
            onAddAccessory={addAccessory}
            onRemoveAccessory={removeAccessory}
          />
        )}
        <CustomAccessorySet
          loading={loading}
          order={order}
          accessories={accessoryTool?.mappedCustomAccessories}
          changes={changes}
          onAddAccessory={addAccessory}
          onRemoveAccessory={removeAccessory}
          isCustomAccessoryOpen={isCustomAccessoryOpen}
          orderId={props?.orderId}
          calculationId={props?.calculationId}
          activeStep={props?.activeStep}
          isUnavailablePimData={props?.isUnavailablePimData}
          menuLoading={props?.menuLoading}
          onClick={props?.onClick}
          onClickSigning={props?.onClickSigning}
          rootUrl={props?.rootUrl}
          isVehicleValidForReserveOrSign={props?.isVehicleValidForReserveOrSign}
          vehicleAvailabilityCode={props.vehicleAvailabilityCode}
        />
      </div>
      <Cart
        exactMatchCount={props.exactMatchCount}
        nonExactMatchCount={props.nonExactMatchCount}
        make={cart?.make}
        modelSeries={cart?.modelSeries}
        prices={finalPrice}
        image={cart?.image}
        data={filterSelectedEquipmentInCart(
          cart?.data,
          getSelectedEquipmentInCart(cart?.data)
        )}
        isVehicleSelected={props?.isVehicleSelected}
        serialNo={props?.selectedSerialNo}
        selectedLocation={props?.vehicleLocation}
        selectedStatus={props?.vehicleStatus}
        isFactoryOrder={props?.isFactoryOrder}
        expectedProductionDate={props?.expectedProductionDate}
        vehicleCount={props?.availableVehicleList?.length}
        isElectricCar={props?.isElectricCar}
        showMBApiWarning={order?.externalTaxCalculation === MBApiCalculationFlag.FETCH_FAILED}
        equipmentDetails={getSelectedEquipmentInCart(cart?.data)}
        footerChildren={
          <MarginCart
            order={props?.order}
            vehicleConfiguration={props?.vehicle}
            articles={articles}
            vatPrices={props?.vatPrices}
            carType={carType}
            isUnavailablePimData={props?.isUnavailablePimData}
          />
        }
        showAbortFactoryOrderAction={props.isShowDeleteButton}
        abortFactoryBorder={{
          buttonIcon: IconType?.NewNextTrash,
          buttonClass: 'red',
          buttonLabel: props.order?.vehicles?.[0]?.serialNo ? 'Fjern bil' : 'Avbryt fabrikkbestilling',
          buttonAction: () => {
            props.setIsShowDeleteModel(!props.isShowDeleteModel);
          },
        }}
      />
      {(props?.isLoading || isTyreAccLoading) && <LoadingSpinner isModal={true} />}
      {props.isShowDeleteModel && (
        <CustomAlertPopup
          isOpen={props.isShowDeleteModel}
          onCloseClick={handleClose}
          deleteOrderData={props.deleteOrderData}
          className='deleteVehicle'  
        ></CustomAlertPopup>
      )}
    </VehicleAccessoriesPageStyled>
  );
};
